import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Pencil, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import type { AdminAccountSummary, AdminAccountsSort } from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import { usePricing } from '../api/pricing.ts';
import { useAccountsList } from '../api/useAdmin.ts';
import { BrandChip } from '../components/BrandChip.tsx';
import { formatLocalTimestamp, formatUsdMicrosOrCentsAsCnyDisplayBalance } from '../components/formatters.ts';
import { CustomerCell } from '../components/identity/CustomerCell.tsx';
import { SignupBrandDialog } from '../components/SignupBrandDialog.tsx';
import { useT } from '../i18n/index.ts';
import {
  Button,
  Checkbox,
  Chip,
  EmptyState,
  IconButton,
  Input,
  PageHeader,
  Select,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
} from '../ui/index.ts';

/**
 * `/customers/accounts` — tenant accounts list.
 *
 * Dispatch Deck (epic #835): `ui/Table` with hairline dividers (no
 * zebra), mono identifier/amount cells, `ui/Chip` status pills.
 *
 * Operator affordances:
 *   - Server-side search (email LIKE) — submit on Enter or button click.
 *   - `Has balance` toggle — filters to accounts with non-zero balance.
 *   - `Low balance` toggle (#1013) — filters to accounts at or below a
 *     zero balance (point-in-time, missing balance row counts as zero;
 *     NOT the cockpit's burn-based chase ranking).
 *   - Sort select — newest-first (default) or balance ascending.
 *   - Pagination (limit=50). Offset-only; total comes from the response.
 *   - Row click → `/customers/accounts/:accountId` detail.
 *
 * URL-state (#1013, same pattern as FinancePnlPage after #1008): the
 * whole filter scope — `email` / `hasBalance` / `lowBalance` / `sort` /
 * `offset` — lives in the URL search params, so hrefs like
 * `?lowBalance=true&sort=balance` (the cockpit chase-list outlink) are
 * deep links instead of the historical silent no-ops, and a reload
 * restores the operator's scope. Each scope tuple is baked into the
 * React Query cache key, so every (email, hasBalance, lowBalance, sort,
 * offset) combination gets its own cache slot. Pagination works by
 * mutating `offset` — we don't show a spinner on re-fetch; TanStack
 * Query keeps the old page rendered and the "Next / Previous" buttons
 * disable while pending.
 */

const PAGE_LIMIT = 50;

function truncateId(value: string | null, head = 8, tail = 4): string {
  if (value === null) return '—';
  if (value.length <= head + tail + 3) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function parseOffsetParam(raw: string | null): number {
  if (raw === null) return 0;
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : 0;
}

function parseSortParam(raw: string | null): AdminAccountsSort {
  return raw === 'balance' ? 'balance' : 'created';
}

interface RowProps {
  readonly row: AdminAccountSummary;
  readonly systemLabel: string;
  readonly tenantLabel: string;
  readonly defaultRatesLabel: string;
  readonly overrideRatesLabel: string;
  readonly editBrandLabel: string;
  readonly cnyPerUsd: number | null;
  readonly onEditBrand: (row: AdminAccountSummary) => void;
}

function AccountRow({
  row,
  systemLabel,
  tenantLabel,
  defaultRatesLabel,
  overrideRatesLabel,
  editBrandLabel,
  cnyPerUsd,
  onEditBrand,
}: RowProps): JSX.Element {
  const detailPath = `/customers/accounts/${encodeURIComponent(row.account_id)}`;
  const overrideCount = Math.max(0, Math.trunc(row.sku_rate_overrides_count ?? 0));
  const hasOverrides = overrideCount > 0;
  return (
    <Tr data-testid={`account-row-${row.account_id}`}>
      <Td emphasis>
        <CustomerCell
          orgId={row.org_id}
          accountId={row.org_id === null ? row.account_id : null}
          testId={`account-row-${row.account_id}-customer`}
          secondary={
            <Link
              to={detailPath}
              className="self-start transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:underline"
              data-testid={`account-row-${row.account_id}-link`}
            >
              {row.email}
            </Link>
          }
        />
      </Td>
      <Td mono title={row.org_id ?? ''}>
        {truncateId(row.org_id)}
      </Td>
      <Td>
        <Chip
          tone={hasOverrides ? 'signal' : 'neutral'}
          lamp={false}
          testId={`account-row-${row.account_id}-sku-rates`}
        >
          <span className="font-mono">{hasOverrides ? overrideCount : '—'}</span>
          <span>{hasOverrides ? overrideRatesLabel : defaultRatesLabel}</span>
        </Chip>
      </Td>
      <Td num emphasis data-testid={`account-row-${row.account_id}-balance`}>
        {formatUsdMicrosOrCentsAsCnyDisplayBalance(row.balance_usd_micros, row.balance_usd_cents, cnyPerUsd)}
      </Td>
      <Td>
        <div className="flex items-center gap-1.5">
          <BrandChip brand={row.signup_brand} testId={`account-row-${row.account_id}-brand`} />
          {row.is_system ? null : (
            <IconButton
              label={editBrandLabel}
              onClick={() => onEditBrand(row)}
              data-testid={`account-row-${row.account_id}-edit-brand`}
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </IconButton>
          )}
        </div>
      </Td>
      <Td>
        {row.is_system ? (
          <Chip tone="neutral" testId={`account-row-${row.account_id}-kind`}>
            {systemLabel}
          </Chip>
        ) : (
          <Chip tone="ok" testId={`account-row-${row.account_id}-kind`}>
            {tenantLabel}
          </Chip>
        )}
      </Td>
      <Td mono>{formatLocalTimestamp(row.created_at, { seconds: true })}</Td>
    </Tr>
  );
}

export function AccountsList(): JSX.Element {
  const t = useT();
  // The URL is the single source of truth for the filter scope (#1013).
  const [params, setParams] = useSearchParams();
  const email = params.get('email') ?? '';
  const hasBalance = params.get('hasBalance') === 'true';
  const lowBalance = params.get('lowBalance') === 'true';
  const sort = parseSortParam(params.get('sort'));
  const offset = parseOffsetParam(params.get('offset'));
  const [draftEmail, setDraftEmail] = useState(email);
  const [brandEditTarget, setBrandEditTarget] = useState<AdminAccountSummary | null>(null);
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;

  const query = useAccountsList({
    limit: PAGE_LIMIT,
    offset,
    email,
    hasBalance,
    lowBalance,
    sort,
  });

  /**
   * Write one scope mutation back to the URL. Default-valued keys are
   * deleted (not written) so the canonical "no filters" URL stays bare;
   * any filter change resets `offset` because the old page position is
   * meaningless under a different predicate.
   */
  function applyScope(
    mutate: (clone: URLSearchParams) => void,
    opts: { resetOffset?: boolean } = {},
  ): void {
    const clone = new URLSearchParams(params);
    mutate(clone);
    if (opts.resetOffset !== false) clone.delete('offset');
    setParams(clone, { replace: true });
  }

  function handleSearch(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const trimmed = draftEmail.trim();
    applyScope((clone) => {
      if (trimmed.length > 0) clone.set('email', trimmed);
      else clone.delete('email');
    });
  }

  function handleToggleHasBalance(checked: boolean): void {
    applyScope((clone) => {
      if (checked) clone.set('hasBalance', 'true');
      else clone.delete('hasBalance');
    });
  }

  function handleToggleLowBalance(checked: boolean): void {
    applyScope((clone) => {
      if (checked) clone.set('lowBalance', 'true');
      else clone.delete('lowBalance');
    });
  }

  function handleSortChange(value: string): void {
    applyScope((clone) => {
      if (value === 'balance') clone.set('sort', 'balance');
      else clone.delete('sort');
    });
  }

  function handleClear(): void {
    setDraftEmail('');
    setParams(new URLSearchParams(), { replace: true });
  }

  function setOffset(next: number): void {
    applyScope(
      (clone) => {
        if (next > 0) clone.set('offset', String(next));
        else clone.delete('offset');
      },
      { resetOffset: false },
    );
  }

  const filtersActive = email.length > 0 || hasBalance || lowBalance;
  const total = query.data?.total ?? 0;
  const currentOffset = query.data?.offset ?? offset;
  const currentLimit = query.data?.limit ?? PAGE_LIMIT;
  const rows = useMemo(() => query.data?.accounts ?? [], [query.data]);
  const hasPrev = currentOffset > 0;
  const hasNext = currentOffset + currentLimit < total;

  return (
    <section data-testid="accounts-list-page" className="flex flex-col gap-6">
      <PageHeader
        title={t('accounts.title')}
        sub={t('accounts.subtitle')}
        actions={
          <form
            className="flex flex-wrap items-center gap-2"
            onSubmit={handleSearch}
            noValidate
            data-testid="accounts-search-form"
          >
            <div className="relative">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-paper-low"
              />
              <Input
                type="search"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.currentTarget.value)}
                placeholder={t('accounts.search.placeholder')}
                aria-label={t('accounts.search.aria_label')}
                data-testid="accounts-search-input"
                className="w-56 pl-9"
              />
            </div>
            <Checkbox
              checked={hasBalance}
              onCheckedChange={handleToggleHasBalance}
              label={t('accounts.has_balance')}
              testId="accounts-has-balance"
            />
            <Checkbox
              checked={lowBalance}
              onCheckedChange={handleToggleLowBalance}
              label={t('accounts.low_balance')}
              testId="accounts-low-balance"
            />
            <Select
              value={sort}
              onValueChange={handleSortChange}
              ariaLabel={t('accounts.sort.aria_label')}
              testId="accounts-sort"
              className="w-44"
              options={[
                { value: 'created', label: t('accounts.sort.created') },
                { value: 'balance', label: t('accounts.sort.balance') },
              ]}
            />
            <Button type="submit" variant="primary" data-testid="accounts-search-submit">
              {t('accounts.search.submit')}
            </Button>
            {filtersActive || sort === 'balance' ? (
              <Button variant="ghost" onClick={handleClear} data-testid="accounts-search-clear">
                {t('accounts.search.clear')}
              </Button>
            ) : null}
          </form>
        }
      />

      {query.isError ? (
        <div
          role="alert"
          data-testid="accounts-error"
          className="flex items-center justify-between gap-4 rounded-lg border border-bad-line bg-bad-dim px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <span className="text-body font-medium text-bad">{t('accounts.error.title')}</span>
            <span className="text-dense text-bad-bright">
              {query.error instanceof OpsApiError
                ? `${query.error.code}: ${query.error.message}`
                : t('common.network_error')}
            </span>
          </div>
          <Button onClick={() => void query.refetch()} data-testid="accounts-retry">
            {t('common.retry')}
          </Button>
        </div>
      ) : null}

      {query.isPending ? (
        <div
          data-testid="accounts-loading"
          className="flex flex-col gap-2 rounded-lg border border-line-soft bg-card p-4"
        >
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-12" />
          ))}
          <span className="sr-only">{t('accounts.loading_aria')}</span>
        </div>
      ) : null}

      {query.isSuccess && rows.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-card">
          <EmptyState
            testId="accounts-empty"
            title={
              filtersActive
                ? t('accounts.empty.filtered.title')
                : t('accounts.empty.initial.title')
            }
            hint={
              filtersActive
                ? t('accounts.empty.filtered.body')
                : t('accounts.empty.initial.body')
            }
          />
        </div>
      ) : null}

      {query.isSuccess && rows.length > 0 ? (
        <>
          <Table testId="accounts-table">
            <THead>
              <tr>
                <Th>{t('accounts.column.email')}</Th>
                <Th>{t('accounts.column.org_id')}</Th>
                <Th>{t('accounts.column.sku_rates')}</Th>
                <Th num>{t('accounts.column.balance')}</Th>
                <Th>{t('accounts.column.brand')}</Th>
                <Th>{t('accounts.column.kind')}</Th>
                <Th>{t('accounts.column.created')}</Th>
              </tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <AccountRow
                  key={row.account_id}
                  row={row}
                  systemLabel={t('accounts.chip.system')}
                  tenantLabel={t('accounts.chip.tenant')}
                  defaultRatesLabel={t('accounts.sku_rates.default')}
                  overrideRatesLabel={t('accounts.sku_rates.overrides')}
                  editBrandLabel={t('brand_edit.action_aria')}
                  cnyPerUsd={cnyPerUsd}
                  onEditBrand={setBrandEditTarget}
                />
              ))}
            </TBody>
          </Table>
          <nav
            aria-label={t('accounts.pagination.aria_label')}
            data-testid="accounts-pagination"
            className="flex items-center justify-between gap-4 rounded-lg border border-line-soft bg-card px-4 py-3 text-dense text-paper-low"
          >
            <span>
              {t('accounts.pagination.range', {
                start: currentOffset + 1,
                end: Math.min(currentOffset + rows.length, total),
                total,
              })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                disabled={!hasPrev || query.isFetching}
                onClick={() => setOffset(Math.max(0, offset - PAGE_LIMIT))}
                data-testid="accounts-pagination-prev"
              >
                {t('common.previous')}
              </Button>
              <Button
                size="sm"
                disabled={!hasNext || query.isFetching}
                onClick={() => setOffset(offset + PAGE_LIMIT)}
                data-testid="accounts-pagination-next"
              >
                {t('common.next')}
              </Button>
            </div>
          </nav>
        </>
      ) : null}

      {brandEditTarget !== null ? (
        <SignupBrandDialog
          accountId={brandEditTarget.account_id}
          email={brandEditTarget.email}
          currentBrand={brandEditTarget.signup_brand}
          onClose={() => setBrandEditTarget(null)}
        />
      ) : null}
    </section>
  );
}
